import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Users, School, Globe, BookOpen, Sprout, Home } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';

const CommunityPage = () => {
  const communityInitiatives = [
    {
      title: "Schools Twinning Projects",
      icon: School,
      image: "https://customer-assets.emergentagent.com/job_roadwise-asia/artifacts/373jtuu5_SAS%20Anthropology.jpg",
      description: "Connecting school communities across Northeast India with partner institutions worldwide",
      highlights: [
        "Interactive workshops and film screenings",
        "Look Up exhibition (astronomy & cosmology)",
        "Ancestral Voices (oral heritage)",
        "Hosted Bookaroo children's literature festival"
      ],
      link: "/community/schools-twinning"
    },
    {
      title: "Indigenous Climate Resilience Programs",
      icon: Sprout,
      image: "https://customer-assets.emergentagent.com/job_roadwise-asia/artifacts/kp2xwc0c_SAS%20Environmental%20Studies.jpeg",
      description: "Co-developing climate adaptation strategies with local communities",
      highlights: [
        "Food sovereignty and seed banks",
        "Water security and infrastructure",
        "Climate observation networks",
        "Traditional knowledge documentation"
      ],
      link: "/community/climate-resilience"
    },
    {
      title: "Fieldwork & Data Collection",
      icon: Globe,
      image: "https://customer-assets.emergentagent.com/job_roadwise-asia/artifacts/ww4wtg1g_SAS%20Philosophy.JPG",
      description: "Mentored student fieldwork integrating local participants",
      highlights: [
        "Ethnographic research training",
        "Open-access field archives",
        "Oral literature repositories",
        "Community-engaged methodology"
      ],
      link: "/community/fieldwork"
    },
    {
      title: "Highland Fellowship",
      icon: Users,
      image: "https://customer-assets.emergentagent.com/job_roadwise-asia/artifacts/19qqt0ph_SAS%20Health%20Sciences.JPG",
      description: "Network of world-class scholars engaged in research and teaching",
      highlights: [
        "27 Highland Fellows worldwide",
        "Fieldwork and mentorship",
        "Research seminars and workshops",
        "Collaborative publications"
      ],
      link: "/fellows"
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-r from-green-900 to-green-700 text-white py-24">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl">
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              Community Engagement
            </h1>
            <p className="text-2xl text-green-100 leading-relaxed">
              Building partnerships, fostering dialogue, and co-creating knowledge with communities across Highland Asia
            </p>
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Our Approach</h2>
            <div className="prose prose-lg max-w-none">
              <p className="text-gray-700 leading-relaxed mb-6">
                The Highland Institute is deeply committed to <strong>community-centered research</strong> and 
                <strong> collaborative partnerships</strong>. We work directly with local researchers, elders, 
                school groups, and grassroots organizations as co-producers of knowledge—not merely as informants.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our community engagement spans <strong>education, climate resilience, cultural heritage, health, 
                and Indigenous knowledge systems</strong>. Every project emphasizes decolonial practice, intercultural 
                exchange, and the revitalization of local knowledge in addressing contemporary challenges.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Initiatives */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Community Initiatives</h2>
            <div className="h-1 w-24 bg-green-600"></div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {communityInitiatives.map((initiative, index) => {
              const IconComponent = initiative.icon;
              return (
                <Card key={index} className="group hover:shadow-2xl transition-all duration-300 overflow-hidden">
                  <div className="relative h-64 overflow-hidden">
                    <img
                      src={initiative.image}
                      alt={initiative.title}
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent"></div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <div className="flex items-center mb-2">
                        <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center mr-3">
                          <IconComponent className="w-5 h-5 text-green-600" />
                        </div>
                        <h3 className="text-2xl font-bold text-white">{initiative.title}</h3>
                      </div>
                    </div>
                  </div>
                  <CardHeader>
                    <CardDescription className="text-gray-700 text-base">
                      {initiative.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2 mb-6">
                      {initiative.highlights.map((highlight, idx) => (
                        <li key={idx} className="flex items-start text-sm">
                          <ArrowRight className="w-4 h-4 text-green-600 mt-0.5 mr-2 flex-shrink-0" />
                          <span className="text-gray-600">{highlight}</span>
                        </li>
                      ))}
                    </ul>
                    <Link to={initiative.link}>
                      <Button className="w-full bg-green-600 hover:bg-green-700 group-hover:scale-105 transition-transform">
                        Learn More
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Community Principles */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">Core Principles</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Users className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Co-Production of Knowledge</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Working with communities as partners and co-researchers, not subjects of study
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Globe className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Decolonial Practice</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Recognizing and revitalizing Indigenous knowledge systems and practices
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                    <Home className="w-6 h-6 text-green-600" />
                  </div>
                  <CardTitle>Long-Term Engagement</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-600">
                    Sustained relationships and commitments beyond individual research projects
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Projects */}
      <section className="py-16 bg-green-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-gray-900 mb-8">Featured Community Projects</h2>
            <div className="space-y-6">
              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-2xl">Bookaroo Nagaland 2018</CardTitle>
                  <CardDescription>India's Pioneering Children's Literature Festival</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">
                    In collaboration with the Government of Nagaland, the Highland Institute hosted Bookaroo, bringing 
                    together celebrated authors, illustrators, poets, and storytellers from across India. The three-day 
                    event engaged over 40 schools and hundreds of children through author visits, interactive storytelling, 
                    and creative workshops.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-2xl">Look Up Exhibition</CardTitle>
                  <CardDescription>Astronomy, Cosmology & Local Knowledge</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">
                    An interdisciplinary exhibition exploring themes of astronomy and cosmology through art, science, 
                    and Indigenous knowledge systems. Students engaged with celestial observation, traditional star lore, 
                    and contemporary astrophysics through hands-on activities and creative expression.
                  </p>
                </CardContent>
              </Card>

              <Card className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <CardTitle className="text-2xl">Ancestral Voices</CardTitle>
                  <CardDescription>Community-Curated Oral Heritage</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="text-gray-700 mb-4">
                    A community-curated exhibition on oral heritage and intergenerational memory, bringing together 
                    elders, youth, and researchers to document and celebrate traditional narratives, songs, and 
                    knowledge practices unique to highland communities.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Get Involved */}
      <section className="py-16 bg-gradient-to-r from-green-600 to-green-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Get Involved</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Interested in collaborating, partnering, or learning more about our community initiatives?
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a href="mailto:info@highlandinstitute.org">
              <Button className="bg-white text-green-600 hover:bg-gray-100 px-8 py-6 text-lg">
                Contact Us
              </Button>
            </a>
            <Link to="/research">
              <Button variant="outline" className="border-white text-white hover:bg-white/10 px-8 py-6 text-lg">
                View Research Projects
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CommunityPage;
